import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ThemeContext = createContext();
const THEME_PREF_KEY = 'themePreference';
const LEGACY_THEME_KEY = 'theme';
const VALID_THEME_PREFERENCES = new Set(['light', 'dark', 'system']);

const getSystemMode = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'system';
  }

  const savedPreference = localStorage.getItem(THEME_PREF_KEY);
  if (VALID_THEME_PREFERENCES.has(savedPreference)) {
    return savedPreference;
  }

  const legacyTheme = localStorage.getItem(LEGACY_THEME_KEY);
  if (legacyTheme === 'light' || legacyTheme === 'dark') {
    return legacyTheme;
  }

  return 'system';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themePreference, setThemePreferenceState] = useState(getInitialThemePreference);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  const mode = themePreference === 'system' ? systemMode : themePreference;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    // Save both new and legacy keys for backwards compatibility
    localStorage.setItem(THEME_PREF_KEY, themePreference);
    localStorage.setItem(LEGACY_THEME_KEY, mode);

    // Update document class for global styling hooks
    document.documentElement.classList.toggle('dark', mode === 'dark');

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.content = mode === 'dark' ? '#0B1220' : '#2563EB';
    }
  }, [themePreference, mode]);

  const setThemePreference = useCallback((nextPreference) => {
    if (!VALID_THEME_PREFERENCES.has(nextPreference)) {
      return;
    }
    setThemePreferenceState(nextPreference);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreferenceState((prevPreference) => {
      const resolvedMode = prevPreference === 'system' ? systemMode : prevPreference;
      return resolvedMode === 'dark' ? 'light' : 'dark';
    });
  }, [systemMode]);

  const value = {
    mode,
    themePreference,
    setThemePreference,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
